import styled from '@xstyled/emotion';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import Tag from '../Tag/index.js';
import { jsx } from '@emotion/react';
import { getUUID } from '../../utils/ids.js';

const STATUS = {
  IDLE: 'idle',
  LOADING: 'loading'
};
const variants = {
  base: _ref => {
    let {
      theme: {
        colors,
        shadows
      }
    } = _ref;
    return `
    padding: 8px;
    border-radius: 4px;
    border: 1px solid ${colors.neutral.borderWeak};
    &:focus-within {
      border: 1px solid ${colors.primary.borderWeak};
      box-shadow: ${shadows.focusPrimary};
    }

    & > * {
      margin: 6px;
    }
  `;
  },
  bordered: _ref2 => {
    let {
      theme: {
        shadows
      }
    } = _ref2;
    return `
    margin-top: 0;
    padding: 8px 0;

    > input:focus {
      box-shadow: ${shadows.focusPrimary};
    }

    > * {
      margin-bottom: 6px;
      &:not(:last-child) {
        margin-right: 6px;
      }
    }
  `;
  },
  'no-border': _ref3 => {
    let {
      theme: {
        shadows
      }
    } = _ref3;
    return `
    &:focus-within {
      box-shadow: ${shadows.focusPrimary};
    }

    > * {
      margin-right: 6px;
      margin-bottom: 6px;
    }
  `;
  }
};
const keysVariant = /*#__PURE__*/Object.keys(variants);
const TagsContainer = styled('div', {
  shouldForwardProp: props => !['variant'].includes(props.toString())
})`
  display: flex;
  flex-wrap: wrap;
  background-color: ${_ref4 => {
  let {
    theme: {
      colors
    }
  } = _ref4;
  return colors.neutral.backgroundWeak;
}};
  ${_ref5 => {
  let {
    variant,
    theme
  } = _ref5;
  return variants[variant] ? variants[variant]({
    theme
  }) : variants.base({
    theme
  });
}}
`;
const StyledInput = styled.input`
  font-size: 16px;
  color: ${_ref6 => {
  let {
    theme: {
      colors
    }
  } = _ref6;
  return colors.neutral.text;
}};
  border: none;
  outline: none;
  background-color: ${_ref7 => {
  let {
    theme: {
      colors
    }
  } = _ref7;
  return colors.neutral.backgroundWeak;
}};
  &::placeholder {
    color: ${_ref8 => {
  let {
    theme: {
      colors
    }
  } = _ref8;
  return colors.neutral.textWeak;
}};
  }
`;

const convertTagArrayToTagStateArray = function (tags) {
  if (tags === void 0) {
    tags = [];
  }

  return tags.map((tag, index) => typeof tag === 'object' ? { ...tag,
    index: getUUID(`tag-${index}`)
  } : {
    index: getUUID(`tag-${index}`),
    label: tag
  });
};

const Tags = _ref9 => {
  let {
    disabled = false,
    id,
    manualInput = true,
    name,
    onChange,
    onChangeError,
    placeholder,
    tags,
    variant = 'base',
    className
  } = _ref9;
  const [tagsState, setTags] = useState(convertTagArrayToTagStateArray(tags));
  const [input, setInput] = useState('');
  const [status, setStatus] = useState({});
  useEffect(() => {
    setTags(convertTagArrayToTagStateArray(tags));
  }, [tags, setTags]);
  const inputRef = useRef(null);

  const dispatchOnChange = newState => {
    const changes = newState.map(tag => typeof tag === 'object' ? tag == null ? void 0 : tag.label : tag);
    onChange == null ? void 0 : onChange(changes);
  };

  const handleContainerClick = () => {
    if (inputRef.current) {
      var _inputRef$current;

      inputRef == null ? void 0 : (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
    }
  };

  const onInputChange = e => setInput(e.target.value);

  const addTag = () => {
    const newTags = input ? [...tagsState, {
      index: getUUID('tag'),
      label: input
    }] : tagsState;
    setInput('');
    setTags(newTags);

    if (newTags.length !== tagsState.length) {
      setStatus({
        [newTags[newTags.length - 1].index]: STATUS.LOADING
      });
    }

    try {
      dispatchOnChange(newTags);
      setStatus({
        [newTags[newTags.length - 1].index]: STATUS.IDLE
      });
    } catch (error) {
      onChangeError == null ? void 0 : onChangeError(error);
      setTags(tagsState);
    }
  };

  const deleteTag = tagIndex => {
    setStatus({
      [tagIndex]: STATUS.LOADING
    });
    const findIndex = tagsState.findIndex(_ref10 => {
      let {
        index
      } = _ref10;
      return index === tagIndex;
    });
    const newTags = [...tagsState];
    newTags.splice(findIndex, 1);

    try {
      dispatchOnChange(newTags);
      setTags(newTags);
      setStatus({
        [tagIndex]: STATUS.IDLE
      });
    } catch (error) {
      onChangeError == null ? void 0 : onChangeError(error);
      setTags(tagsState);
    }
  };

  const handleInputKeydown = e => {
    var _inputRef$current2;

    // 32 = Space | 13 = Enter | 8 = Backspace
    // https://www.w3.org/TR/uievents-key/#control
    const key = e.key || e.keyCode;
    const space = [' ', 32];
    const enter = ['Enter', 13];
    const tab = ['Tab', 9];
    const backspace = ['Backspace', 8];

    if (space.includes(key) || enter.includes(key) || tab.includes(key)) {
      addTag();
      e.preventDefault();
    }

    if (backspace.includes(key) && (inputRef == null ? void 0 : (_inputRef$current2 = inputRef.current) == null ? void 0 : _inputRef$current2.selectionStart) === 0 && tagsState.length) {
      e.preventDefault();
      deleteTag(tagsState[tagsState.length - 1].index);
    }
  };

  const handlePaste = e => {
    var _e$clipboardData;

    e.preventDefault();
    const newTags = [...tagsState, {
      index: getUUID('tag'),
      label: e == null ? void 0 : (_e$clipboardData = e.clipboardData) == null ? void 0 : _e$clipboardData.getData('Text')
    }];
    setTags(newTags);
    setStatus({
      [newTags.length - 1]: STATUS.LOADING
    });

    try {
      dispatchOnChange(newTags);
      setStatus({
        [newTags.length - 1]: STATUS.IDLE
      });
    } catch (error) {
      onChangeError == null ? void 0 : onChangeError(error);
      setTags(tagsState);
    }
  };

  return jsx(TagsContainer, {
    onClick: handleContainerClick,
    variant: variant,
    onBlur: addTag,
    className: className
  }, tagsState.map(tag => jsx(Tag, {
    variant: variant === 'no-border' ? 'base' : variant,
    disabled: disabled,
    key: tag.index,
    isLoading: status[tag.index] === STATUS.LOADING,
    onClose: e => {
      e.stopPropagation();
      deleteTag(tag.index);
    }
  }, tag.label)), !disabled && manualInput ? jsx(StyledInput, {
    id: id,
    name: name,
    "aria-label": name,
    type: "text",
    placeholder: !tagsState.length ? placeholder : '',
    value: input,
    onChange: onInputChange,
    onKeyDown: handleInputKeydown,
    onPaste: handlePaste,
    ref: inputRef
  }) : null);
};

Tags.defaultProps = {
  tags: []
};
Tags.propTypes = {
  disabled: PropTypes.bool,
  id: PropTypes.string,
  manualInput: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onChangeError: PropTypes.func,
  placeholder: PropTypes.string,
  tags: /*#__PURE__*/PropTypes.arrayOf( /*#__PURE__*/PropTypes.oneOfType([PropTypes.string, /*#__PURE__*/PropTypes.shape({
    label: PropTypes.string
  })])),
  variant: /*#__PURE__*/PropTypes.oneOf(keysVariant)
};
var Tags$1 = Tags;

export { Tags$1 as default };
